<template>
  <el-dialog
    v-model="localVisible"
    @close="onClose"
    :close-on-press-escape="false"
    :show-close="false"
    append-to-body
    destroy-on-close
    class="appointment-modal"
    width="500px"
  >
    <div class="appointment-modal-body">
      <button class="modal-close-btn" @click="onClose">×</button>
      <div class="appointment-header-visual">
        <div class="header-row">
          <div class="header-item">
            <el-icon><Calendar /></el-icon>
            <span>{{ displayDate }}</span>
            <span class="header-muted">
              ({{ formattedDate }}
              <span class="date-weekday" :class="{ weekend: isWeekend }">{{ weekdayLabel }}</span>)
            </span>
          </div>
          <div class="header-item">
            <el-icon><Clock /></el-icon>
            <span>{{ headerTimeLabel }}</span>
          </div>
        </div>
        <div class="header-row">
          <div class="header-item">
            <el-icon><UserFilled /></el-icon>
            <span>{{ form.employee.user_name }}</span>
          </div>
          <div class="header-item">
            <el-icon><Folder /></el-icon>
            <span>{{ form.employee.room_name }}</span>
          </div>
        </div>
        <div class="header-row status-row">
          <div class="header-item">
            <el-icon><Clock /></el-icon>
            <span>{{ form.duration || totalDuration }} мин</span>
          </div>
        </div>
      </div>

      <el-form :model="form" label-position="top" class="appointment-form">
        <template v-if="form.client">
          <div class="client-info-card">
            <div class="client-row client-phone">
              <el-icon><Phone /></el-icon>
              <span>{{ form.client.phone }}</span>
            </div>
            <div class="client-row client-name">
              <el-icon><UserFilled /></el-icon>
              <span>{{ form.client.fio }}</span>
              <el-link @click="form.client = null" type="danger" class="client-selected-reset">Сбросить</el-link>
            </div>
          </div>
        </template>
        <el-form-item v-else label="Клиент">
          <el-autocomplete
            v-model="clientSearch"
            :fetch-suggestions="searchClientsFn"
            placeholder="Телефон или имя"
            @select="selectClient"
            @change="onClientInput"
            :trigger-on-focus="false"
            popper-class="appointment-client-search"
            style="width: 100%;"
          >
            <template #default="{ item }">
              <div v-if="item.create" class="autocomplete-create">
                <el-icon><Plus /></el-icon>
                <span>Создать клиента: <b>{{ item.value }}</b></span>
              </div>
              <div v-else class="autocomplete-client-item">
                <div class="client-row">
                  <el-icon><UserFilled /></el-icon>
                  <span>{{ item.fio }}</span>
                </div>
                <div class="client-row" v-if="item.phone">
                  <el-icon><Phone /></el-icon>
                  <span>{{ item.phone }}</span>
                </div>
              </div>
            </template>
          </el-autocomplete>
        </el-form-item>

        <el-form-item class="service-form-item">
          <ServiceMultiSelect
            v-model="form.services"
            :services="services"
            :remote="true"
            :city-id="form.employee?.city_id"
          />
        </el-form-item>

        <el-form-item label="Длительность">
          <div class="duration-control">
            <el-button class="duration-btn" @click="changeDuration(-15)">-</el-button>
            <div class="duration-value">{{ form.duration || 0 }} мин.</div>
            <el-button class="duration-btn" @click="changeDuration(15)">+</el-button>
          </div>
        </el-form-item>

        <el-form-item label="Комментарий">
          <el-input
            type="textarea"
            v-model="form.comment"
            placeholder="Комментарий"
          />
        </el-form-item>
      </el-form>
      <div class="modal-footer-actions">
        <el-button @click="onClose" class="btn-cancel">Отмена</el-button>
        <el-button type="primary" @click="save" :disabled="!canSave" class="btn-save">Записать</el-button>
      </div>
    </div>

    <QuickClientCreate
      :visible="showQuickCreate"
      :city-id="form.employee.city_id"
      :default-fio="quickCreateForm.fio"
      :default-phone="quickCreateForm.phone"
      @save="handleQuickCreateClient"
      @close="showQuickCreate = false"
    />
  </el-dialog>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import QuickClientCreate from '@/components/util/calendar/QuickClientCreate.vue'
import { Calendar, Clock, Folder, Phone, Plus, UserFilled } from '@element-plus/icons-vue'
import ServiceMultiSelect from '@/components/util/calendar/ServiceMultiSelect.vue'

export default {
  name: 'AppointmentCreateModal',
  components: { Plus, Phone, ServiceMultiSelect, Clock, UserFilled, Folder, QuickClientCreate, Calendar },
  props: {
    visible: { type: Boolean, required: true },
    date: { type: String, required: true },
    time: { type: String, required: true, default: '' },
    employee: { type: Object, required: true }
  },
  data() {
    return {
      quickCreateForm: {
        fio: '',
        phone: ''
      },
      localVisible: this.visible,
      form: {
        date: this.date,
        time: this.time,
        employee: this.employee,
        services: [],
        serviceIds: [],
        duration: 0,
        client: null,
        comment: ''
      },
      clientSearch: '',
      showQuickCreate: false,
      lastSuggestions: [],
      showCreateClientButton: false,
      durationEdited: false
    }
  },
  computed: {
    displayDate() {
      if (!this.form.date) return ''
      const parts = this.form.date.split('-')
      if (parts.length !== 3) return this.form.date
      return `${parts[2]}.${parts[1]}.${parts[0]}`
    },
    headerTimeLabel() {
      const duration = this.form.duration || this.totalDuration
      if (!this.form.time || !duration) return this.form.time
      return `${this.form.time}–${this.addMinutes(this.form.time, duration)}`
    },
    weekdayLabel() {
      if (!this.date) return ''
      const d = new Date(this.date)
      if (isNaN(d.getTime())) return ''
      const weekdays = ['Вс', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб']
      return weekdays[d.getDay()]
    },
    isWeekend() {
      if (!this.date) return false
      const d = new Date(this.date)
      if (isNaN(d.getTime())) return false
      return d.getDay() === 0 || d.getDay() === 6
    },
    ...mapState('clients', ['search_clients']),
    ...mapState('services', ['services']),
    canSave() {
      return this.form.services.length > 0 && this.form.client
    },
    totalDuration() {
      return this.form.services.reduce((sum, s) => sum + (+s.duration || 0), 0)
    },
    formattedDate() {
      const months = [
        'Января', 'Февраля', 'Марта', 'Апреля', 'Мая', 'Июня',
        'Июля', 'Августа', 'Сентября', 'Октября', 'Ноября', 'Декабря'
      ]
      const [, month, day] = this.form.date.split('-')
      const monthName = months[parseInt(month, 10) - 1]
      return `${day} ${monthName}`
    }
  },
  watch: {
    visible(val) {
      this.localVisible = val
      if (val) this.resetForm()
    },
    localVisible(val) {
      if (!val) this.$emit('update:visible', false)
    },
    date(val) {
      this.form.date = val
    },
    time(val) {
      this.form.time = val
    },
    employee(val) {
      this.form.employee = val
    },
    'form.services': {
      handler() {
        if (!this.durationEdited) {
          this.form.duration = this.totalDuration
        }
      },
      immediate: true,
      deep: true
    }
  },
  methods: {
    ...mapActions('clients', ['searchClients']),
    async searchClientsFn(query, cb) {
      if (!query || query.length < 3) return cb([])

      await this.searchClients({ query })
      let results = (this.search_clients || []).map(c => ({
        ...c,
        value: c.fio + (c.phone ? ` (${c.phone})` : '')
      }))

      this.lastSuggestions = results

      if (results.length === 0) {
        results = [{ value: query, create: true }]
      }

      cb(results)
    },
    onClientInput(val) {
      this.showCreateClientButton = val && val.length >= 3 && this.lastSuggestions.length === 0
    },
    selectClient(item) {
      if (item.create) {
        this.openQuickCreateClient(this.clientSearch)
      } else {
        this.form.client = item
        this.clientSearch = ''
        this.showCreateClientButton = false
      }
    },
    openQuickCreateClient(query) {
      let phone = ''
      let fio = ''
      if (/^\d[\d\-+()\s]+$/.test(query)) phone = query
      else fio = query
      this.quickCreateForm = { fio, phone }
      this.showQuickCreate = true
    },
    async handleQuickCreateClient(payload) {
      const res = await this.$store.dispatch('clients/saveQuickClient', payload)

      const newClient = res && res.id
        ? { id: res.id, fio: payload.fio, phone: payload.phone }
        : { fio: payload.fio, phone: payload.phone }
      this.form.client = newClient
      this.clientSearch = newClient.fio
      this.showQuickCreate = false
      this.showCreateClientButton = false
    },
    resetForm() {
      this.form = {
        date: this.date,
        time: this.time,
        employee: this.employee,
        services: [],
        duration: 0,
        client: null,
        comment: ''
      }
      this.clientSearch = ''
      this.showCreateClientButton = false
      this.lastSuggestions = []
      this.durationEdited = false
    },
    onClose() {
      this.localVisible = false
    },
    changeDuration(step) {
      this.durationEdited = true
      const next = (this.form.duration || 0) + step
      this.form.duration = Math.max(0, next)
    },
    addMinutes(time, min) {
      let [h, m] = time.split(':').map(Number)
      m += min
      h += Math.floor(m / 60)
      m = m % 60
      return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
    },
    async save() {
      const totalDuration = this.form.duration || (this.form.services || []).reduce((sum, srv) => sum + (+srv.duration || 0), 0)
      const endTime = this.addMinutes(this.form.time, totalDuration)

      const appointment = {
        id: this.form.id || null,
        branch_id: this.form.employee.branch_id,
        city_id: this.form.employee.city_id,
        room_id: this.form.employee.room_id,
        user_id: this.form.employee.user_id,
        user_name: this.form.employee.user_name,
        client_id: this.form.client.id,
        client_name: this.form.client.fio,
        client_phone: this.form.client.phone,
        date: this.form.date,
        start_time: this.form.time,
        end_time: endTime,
        services: this.form.services,
        comment: this.form.comment,
        duration_time: totalDuration,
        status: 'future',
        color: 'var(--ui-color-brand-strong)'
      }
      await this.$store.dispatch('appointments/saveAppointment', appointment)
      this.onClose()
    }
  }
}
</script>

<style scoped>
.appointment-modal-body {
  background: linear-gradient(rgba(0, 108, 254, 0.22), rgba(0, 108, 254, 0.22)), linear-gradient(151deg, #15264e 0%, #030b1b 100%);
  border-radius: 8px !important;
  padding: 18px 20px 16px !important;
  position: relative;
}

.date-weekday {
  font-size: 14px;
  color: #7ab6ff;
  font-weight: 700;
}

.date-weekday.weekend {
  color: #ff8f8f;
}

.header-muted {
  color: #9eb6d9;
}

.appointment-header-visual {
  margin-bottom: 14px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.header-row {
  display: flex;
  gap: 24px;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
}

.header-item {
  display: flex;
  align-items: center;
  font-size: 14px;
  color: #dce8ff;
  gap: 8px;
  line-height: 1.25;
}

.header-item :deep(.el-icon),
.client-row :deep(.el-icon) {
  color: #031230 !important;
}

.header-item :deep(.el-icon svg),
.client-row :deep(.el-icon svg) {
  color: #031230 !important;
  fill: #031230 !important;
  stroke: #031230 !important;
}

.status-row {
  margin-top: 2px;
}

.status-row .header-item > span {
  color: #57c7ff;
}

.header-row:nth-child(2) .header-item:last-child > span {
  color: #57c7ff;
}

.appointment-form :deep(.el-form-item__label) {
  color: #dce8ff;
  font-weight: 700;
  font-size: 14px;
  line-height: 1.2;
  margin-bottom: 8px;
  padding: 0 !important;
}

.appointment-form :deep(.el-form-item) {
  margin-bottom: 14px;
}

.appointment-form :deep(.el-input__wrapper),
.appointment-form :deep(.el-textarea__inner),
.appointment-form :deep(.el-select__wrapper) {
  background: linear-gradient(180deg, #4b6fba 0%, #385aa1 100%);
  box-shadow: 0 0 0 1px #496fb7 inset !important;
  color: #eaf3ff;
  min-height: 40px;
  border-radius: 5px !important;
}

.appointment-form :deep(.el-input__inner),
.appointment-form :deep(.el-textarea__inner) {
  color: #eaf3ff;
}

.appointment-form :deep(.el-textarea__inner::placeholder),
.appointment-form :deep(.el-input__inner::placeholder) {
  color: #8ac1ff;
}

.autocomplete-create {
  display: flex;
  align-items: center;
  color: #52c9ff;
  gap: 8px;
}

.client-selected-block {
  margin-top: 10px;
  background:
    linear-gradient(rgb(16 53 117 / 49%), rgb(16 53 117 / 49%)),
    linear-gradient(151deg, #15264e 0%, #030b1b 75%);
  border-radius: 5px;
  border: none;
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 12px;
  width: 100%;
}

.client-info-card {
  border: none;
  border-radius: 5px;
  padding: 14px 16px;
  margin-bottom: 10px;
  background:
    linear-gradient(rgb(16 53 117 / 49%), rgb(16 53 117 / 49%)),
    linear-gradient(151deg, #15264e 0%, #030b1b 75%);
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.create-client-card {
  min-height: 78px;
  justify-content: center;
}

.client-row {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 16px;
  color: #dce8ff;
}

.client-selected-reset {
  margin-left: auto;
  font-size: 13px;
}

.duration-control {
  display: flex;
  align-items: center;
  gap: 4px;
}

.duration-btn {
  width: 34px !important;
  min-width: 34px !important;
  height: 34px !important;
  min-height: 34px !important;
  font-size: 24px !important;
  border-radius: 6px !important;
  background: var(--appointment-control-duration);
  border: none;
  color: var(--appointment-control-text);
}

.duration-value {
  min-width: 80px !important;
  height: 34px !important;
  line-height: 24px !important;
  text-align: center;
  background: var(--appointment-value);
  color: var(--appointment-value-text);
  border-radius: 6px !important;
  font-size: 16px !important;
  font-weight: 700;
  padding: 5px 10px;
}

.modal-footer-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 18px;
}

.btn-cancel,
.btn-save {
  min-width: 88px !important;
  width: auto !important;
  height: 34px !important;
  min-height: 34px !important;
  border-radius: 5px !important;
  font-size: 13px !important;
  font-weight: 700;
  padding: 0 20px !important;
}

.btn-cancel {
  background: var(--appointment-secondary);
  color: var(--appointment-secondary-text);
  border: none;
  box-shadow: 0 0 0 1px rgba(118, 159, 232, 0.25) inset;
}

.btn-save {
  background: var(--appointment-primary) !important;
  color: var(--appointment-primary-text) !important;
  border: none;
}

:global(.appointment-modal .el-button),
:global(.appointment-modal .el-link),
:global(.appointment-modal .el-input__wrapper),
:global(.appointment-modal .el-textarea__inner),
:global(.appointment-modal .el-select__wrapper),
:global(.appointment-modal .el-input-number .el-input__wrapper) {
  transition: background-color .18s ease, color .18s ease, border-color .18s ease, box-shadow .18s ease, filter .18s ease;
}

:global(.appointment-modal .el-button:not(.el-button--primary):not(.el-button--success):not(.el-button--warning):not(.el-button--danger):hover),
:global(.appointment-modal .el-button:not(.el-button--primary):not(.el-button--success):not(.el-button--warning):not(.el-button--danger):focus-visible),
:global(.appointment-modal .el-button.is-text:hover),
:global(.appointment-modal .el-button.is-text:focus-visible) {
  background: linear-gradient(rgb(16 53 117 / 52%), rgb(16 53 117 / 52%)) !important;
  border-color: #3f6fb8 !important;
  color: #dce8ff !important;
  box-shadow: 0 0 0 1px rgb(63 111 184 / 55%) inset !important;
}

:global(.appointment-modal .el-button.el-button--primary:hover),
:global(.appointment-modal .el-button.el-button--primary:focus-visible) {
  filter: brightness(.95);
}

:global(.appointment-modal .el-button:active) {
  filter: brightness(.9);
}

:global(.appointment-modal .el-input__wrapper:hover),
:global(.appointment-modal .el-select__wrapper:hover),
:global(.appointment-modal .el-input-number .el-input__wrapper:hover),
:global(.appointment-modal .el-textarea__inner:hover),
:global(.appointment-modal .el-input__wrapper.is-focus),
:global(.appointment-modal .el-select__wrapper.is-focused),
:global(.appointment-modal .el-input-number .el-input__wrapper.is-focus) {
  box-shadow: 0 0 0 1px #5a82ca inset !important;
  background: linear-gradient(180deg, #4f72bb 0%, #3c5fa7 100%) !important;
}

:global(.appointment-modal .el-link:hover),
:global(.appointment-modal .el-link:focus-visible) {
  color: #57c7ff !important;
}

:global(.appointment-modal .el-table__body tr:hover > td.el-table__cell) {
  background: rgba(24, 53, 104, 0.55) !important;
}

:global(.appointment-modal) {
  width: 500px !important;
  max-width: calc(100vw - 32px) !important;
  background: transparent !important;
  border-radius: 8px;
  box-shadow: none !important;
  border: none !important;
  margin-top: 7vh !important;
}

:global(.appointment-modal .appointment-modal-body) {
  border-radius: 8px !important;
  padding: 18px 20px 16px !important;
}

:global(.appointment-modal .appointment-form .el-form-item) {
  margin-bottom: 14px !important;
}

:global(.appointment-modal .service-form-item) {
  margin-bottom: 14px !important;
}

:global(.appointment-modal .client-info-card .el-input__wrapper) {
  min-height: 40px !important;
  height: 40px !important;
  border-radius: 5px !important;
}

:global(.appointment-modal .appointment-form .el-form-item__label) {
  height: auto !important;
  min-height: 0 !important;
  padding: 0 !important;
  margin-bottom: 8px !important;
  font-size: 14px !important;
  line-height: 1.2 !important;
}

:global(.appointment-modal .appointment-form .el-input__wrapper),
:global(.appointment-modal .appointment-form .el-select__wrapper) {
  min-height: 40px !important;
  height: 40px !important;
  border-radius: 5px !important;
}

:global(.appointment-modal .appointment-form .el-input__inner) {
  height: 38px !important;
  line-height: 38px !important;
  font-size: 14px !important;
}

:global(.appointment-modal .appointment-form .el-textarea__inner) {
  min-height: 54px !important;
  height: 54px !important;
  border-radius: 5px !important;
  font-size: 14px !important;
}

:global(.appointment-modal .duration-control) {
  gap: 4px !important;
}

:global(.appointment-modal .duration-btn.el-button) {
  width: 34px !important;
  min-width: 34px !important;
  height: 34px !important;
  min-height: 34px !important;
  padding: 0 !important;
  border-radius: 6px !important;
  font-size: 24px !important;
}

:global(.appointment-modal .duration-value) {
  min-width: 80px !important;
  height: 34px !important;
  padding: 5px 10px !important;
  border-radius: 6px !important;
  font-size: 16px !important;
  line-height: 24px !important;
}

:global(.appointment-modal .modal-footer-actions) {
  gap: 10px !important;
  margin-top: 18px !important;
}

:global(.appointment-modal .btn-cancel.el-button),
:global(.appointment-modal .btn-save.el-button) {
  min-width: 88px !important;
  height: 34px !important;
  min-height: 34px !important;
  padding: 0 20px !important;
  border-radius: 5px !important;
  font-size: 13px !important;
}

:global(.appointment-modal .el-dialog__header) {
  background: transparent !important;
  padding: 0;
  margin: 0;
}

:global(.appointment-modal .el-dialog__body) {
  background: transparent !important;
  padding: 0;
}

:global(.appointment-modal .el-dialog__footer) {
  background: transparent !important;
  padding: 0;
  display: none;
}

:global(.appointment-modal .el-dialog__headerbtn .el-dialog__close) {
  color: #0c1f49;
  font-size: 24px;
}

.modal-close-btn {
  position: absolute;
  right: 8px;
  top: 7px;
  width: 28px;
  height: 28px;
  border: none;
  background: transparent;
  color: #031230;
  font-size: 24px;
  line-height: 22px;
  cursor: pointer;
  z-index: 2;
}

.appointment-modal-body :deep(.el-icon),
.appointment-modal-body :deep(.el-icon svg),
.appointment-modal-body :deep(.el-icon svg path) {
  color: #031230 !important;
  fill: #031230 !important;
  stroke: #031230 !important;
}
</style>
